import { useAuth } from "@clerk/react";
import { useCallback, useState } from "react";

import { useWaitlist } from "../../hooks/useWaitlist";
import { leaveWaitlist } from "../../lib/waitlist-api";
import { ReferralShareSheet } from "../referral/referral-share-sheet";
import { useToast } from "../ui/toast";
import { WaitlistPositionCard } from "./WaitlistPositionCard";

/**
 * Live geo-waitlist position card (M11-C2). Self-fetches the owner-scoped
 * position and renders NOTHING when the caller is not on a queue — so it can be
 * dropped beside PassportPublishBlock with zero effect on the common
 * (delivered-to-browse) case.
 *
 * The refer CTA (`onShare`, plan 317 task 1483) opens the shared
 * `ReferralShareSheet` — the SAME minted `?referral_token=` link as Settings
 * and the dashboard banner (M11-C5's consumer_referral lane; never the
 * agent-invite `?ref=` lane, which collided with agent-preview and never
 * attributed).
 */
export function WaitlistPositionCardContainer() {
  const { waitlist, refresh } = useWaitlist();
  const { getToken } = useAuth();
  const { pushToast } = useToast();

  const onLeave = useCallback(async () => {
    try {
      await leaveWaitlist(getToken);
      await refresh();
      pushToast({ title: "You've left the waitlist." });
    } catch {
      pushToast({ title: "Couldn't update the waitlist — try again." });
    }
  }, [getToken, refresh, pushToast]);

  const [shareOpen, setShareOpen] = useState(false);

  if (!waitlist) return null;

  return (
    <>
      <WaitlistPositionCard
        waitlist={waitlist}
        onShare={() => setShareOpen(true)}
        onLeave={() => void onLeave()}
      />
      <ReferralShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        waitlistArea={waitlist.area_label}
      />
    </>
  );
}
